import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { appConfig } from '@/config/app-config';
import type {
  ApiResponse,
  ListObjectsResponse,
  GetUploadUrlResponse,
  GetDownloadUrlResponse,
  SyncStatusResponse,
  SyncErrorsResponse,
} from '@/types';

// Axiosインスタンス作成
const apiClient: AxiosInstance = axios.create({
  baseURL: appConfig.apiEndpoint,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター: 認証トークンを自動付与
apiClient.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      config.headers['Authorization'] = token;
    }
  } catch (error) {
    console.error('Failed to get auth token:', error);
  }
  return config;
});

// レスポンスインターセプター: エラーハンドリング
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 認証エラー時はログインページへリダイレクト
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// API関数

/**
 * S3オブジェクト一覧取得
 */
export async function listObjects(prefix: string, continuationToken?: string): Promise<ApiResponse<ListObjectsResponse>> {
  const params = new URLSearchParams({ prefix });
  if (continuationToken) {
    params.append('continuationToken', continuationToken);
  }

  const response = await apiClient.get<ApiResponse<ListObjectsResponse>>(`/s3/objects?${params.toString()}`);
  return response.data;
}

/**
 * フォルダ作成
 */
export async function createFolder(folderPath: string): Promise<ApiResponse<{ folderPath: string }>> {
  const response = await apiClient.post<ApiResponse<{ folderPath: string }>>('/s3/folders', {
    folderPath,
  });
  return response.data;
}

/**
 * オブジェクト削除
 */
export async function deleteObjects(keys: string[]): Promise<ApiResponse<{ deletedCount: number }>> {
  const response = await apiClient.delete<ApiResponse<{ deletedCount: number }>>('/s3/objects', {
    data: { keys },
  });
  return response.data;
}

/**
 * オブジェクト移動
 */
export async function moveObject(sourceKey: string, destinationKey: string): Promise<ApiResponse<void>> {
  const response = await apiClient.post<ApiResponse<void>>('/s3/move', {
    sourceKey,
    destinationKey,
  });
  return response.data;
}

/**
 * フォルダ名変更
 */
export async function renameFolder(sourcePath: string, destinationPath: string): Promise<ApiResponse<void>> {
  const response = await apiClient.post<ApiResponse<void>>('/s3/rename-folder', {
    sourcePath,
    destinationPath,
  });
  return response.data;
}

/**
 * アップロード用署名付きURL取得
 */
export async function getUploadUrl(key: string, contentType: string): Promise<ApiResponse<GetUploadUrlResponse>> {
  const response = await apiClient.post<ApiResponse<GetUploadUrlResponse>>('/s3/upload-url', {
    key,
    contentType,
  });
  return response.data;
}

/**
 * ダウンロード用署名付きURL取得
 */
export async function getDownloadUrl(key: string): Promise<ApiResponse<GetDownloadUrlResponse>> {
  const params = new URLSearchParams({ key });
  const response = await apiClient.get<ApiResponse<GetDownloadUrlResponse>>(`/s3/download-url?${params.toString()}`);
  return response.data;
}

/**
 * Kendra同期開始
 */
export async function syncKendra(dataSourceId: string): Promise<ApiResponse<{ executionId: string; status: string }>> {
  const response = await apiClient.post<ApiResponse<{ executionId: string; status: string }>>('/kendra/sync', {
    dataSourceId,
  });
  return response.data;
}

/**
 * Kendra同期ステータス取得
 */
export async function getSyncStatus(dataSourceId: string): Promise<ApiResponse<SyncStatusResponse>> {
  const params = new URLSearchParams({ dataSourceId });
  const response = await apiClient.get<ApiResponse<SyncStatusResponse>>(`/kendra/sync-status?${params.toString()}`);
  return response.data;
}

/**
 * Kendra同期エラーログ取得（最後の同期ジョブのエラーのみ）
 */
export async function getSyncErrors(
  dataSourceId: string
): Promise<ApiResponse<SyncErrorsResponse>> {
  const params = new URLSearchParams({ dataSourceId });
  const response = await apiClient.get<ApiResponse<SyncErrorsResponse>>(`/kendra/sync-errors?${params.toString()}`);
  return response.data;
}

/**
 * S3への直接アップロード（署名付きURL経由）
 */
export async function uploadFileToS3(uploadUrl: string, file: File, onProgress?: (progress: number) => void): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });
}

export { apiClient };
